import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { TRIAL_STATS_DEFAULTS } from '../utils/stats';
import { UpsellStats } from '../components/upsell/UpsellStats';
import { UpsellSocialProof } from '../components/upsell/UpsellSocialProof';
import { UpsellPricing } from '../components/upsell/UpsellPricing';
import { UpsellFAQ } from '../components/upsell/UpsellFAQ';
import { UpsellFeatureTable } from '../components/upsell/UpsellFeatureTable';
import { UpsellFinalCta } from '../components/upsell/UpsellFinalCta';
import { UpsellStickyCta } from '../components/upsell/UpsellStickyCta';

interface GuestStats {
  sessionsCompleted: number;
  wordsRead: number;
  maxWpm: number;
  avgWpm: number;
  timeSaved: number;
  sessions: number;
  booksCompleted: number;
  genresRead: string[];
  timeSpentSeconds: number;
}

const UpsellPage: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Speed Reading Premium & Reading App Upgrade Plans | Flash-Read',
    description: 'Upgrade to Flash-Read premium for unlimited stories, achievements, streaks, cloud sync, and advanced analytics. Choose a reading app subscription plan — Starter, Reader, or Ultra.',
  });

  const [stats, setStats] = useState<GuestStats>(TRIAL_STATS_DEFAULTS);

  // Load guest stats from localStorage
  useEffect(() => {
    const savedStats = localStorage.getItem('flashread_stats');
    if (savedStats) {
      try {
        const parsed = JSON.parse(savedStats);
        const wordsRead = parsed.wordsRead || 0;
        const maxWpm = parsed.maxWpm || 0;
        const avgWpm = parsed.avgWpm || 0;
        const sessions = parsed.sessions || 0;
        const booksCompleted = parsed.booksCompleted || 0;
        const genresRead = parsed.genresRead || [];
        const timeSpentSeconds = parsed.timeSpentSeconds || 0;
        // Estimate time saved: at 200 WPM vs their avg speed
        const timeSavedMinutes = wordsRead > 0 && avgWpm > 0
          ? (wordsRead / 200) - (wordsRead / avgWpm)
          : 0;

        setStats({
          sessionsCompleted: 3,
          wordsRead,
          maxWpm,
          avgWpm,
          timeSaved: Math.round(timeSavedMinutes * 60), // seconds
          sessions,
          booksCompleted,
          genresRead,
          timeSpentSeconds,
        });
      } catch {
        // Use defaults
      }
    }
  }, []);

  const handleNavigate = (page: string) => {
    navigate(page);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section 1: Header */}
        <div className="text-center mb-8 animate-fade-in">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>

          <div className="inline-flex items-center justify-center p-3 rounded-full mb-6 bg-amber-100 dark:bg-amber-900/30">
            <CheckCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-6">
            Your trial is complete —<br className="hidden md:block"/>
            <span className="text-primary">here's what you accomplished</span>
          </h1>

          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            You've read 3 free stories with RSVP speed reading. See your results and unlock the full experience.
          </p>
        </div>

        {/* Escape hatch */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors underline"
          >
            ← Return Home
          </button>
        </div>

        {/* Keyword section */}
        <div className="stories-intro mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-3xl mx-auto">
          <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed text-center">
            <span className="keyword-highlight">Speed reading upgrade</span> with{' '}
            <span className="keyword-highlight">RSVP premium</span> technology.{' '}
            <span className="keyword-highlight">Reading app subscription</span> for{' '}
            <span className="keyword-highlight">speed reading premium</span> results.{' '}
            <span className="keyword-highlight">Reading app premium plans</span> —{' '}
            <span className="keyword-highlight">unlock speed reading</span>.
          </p>
        </div>

        {/* Section 2: Cognitive Adaptation Card */}
        <div className="cognitive-adapt-card mb-12">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            Your Brain Has Already Adapted
          </h2>
          <p className="text-slate-700 dark:text-slate-300 mb-6 leading-relaxed">
            During your 3 sessions, your visual cortex began processing single-word recognition faster. This is the same cognitive adaptation that lets speed readers maintain 2-3x reading speeds. Stopping now means losing that momentum.
          </p>
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700 dark:text-slate-300">Your eyes learned to fixate on a single point</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700 dark:text-slate-300">Your brain started recognizing words faster</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700 dark:text-slate-300">Comprehension stayed high even as speed increased</span>
            </li>
          </ul>
          <p className="text-xs text-slate-500 dark:text-slate-400 italic">
            Based on research in Rapid Serial Visual Presentation (RSVP) cognition
          </p>
        </div>

        {/* Section 3: Stats & Results */}
        <UpsellStats
          wordsRead={stats.wordsRead}
          maxWpm={stats.maxWpm}
          avgWpm={stats.avgWpm}
          timeSaved={stats.timeSaved}
          sessions={stats.sessions}
          booksCompleted={stats.booksCompleted}
          genresRead={stats.genresRead}
          timeSpentSeconds={stats.timeSpentSeconds}
        />

        {/* Section 4: Social Proof */}
        <UpsellSocialProof />

        {/* Section 5: What You Unlock — Free vs Premium comparison */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-4">
            What You Get With Premium
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 text-center mb-8 max-w-2xl mx-auto">
            See what you're missing — and what unlocks when you upgrade.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Free column */}
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 opacity-60">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Free</h3>
              <ul className="space-y-3">
                {['3 stories', 'No progress saving', 'No achievements', 'No streaks', 'No analytics', 'Session-only history'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <span className="text-red-500">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {/* Premium column */}
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-blue-500 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold">
                Premium
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 mt-2">Premium</h3>
              <ul className="space-y-3">
                {[
                  'Unlimited stories + uploads',
                  'Persistent progress + cloud sync',
                  '100+ achievements + XP system',
                  'Streak tracking + multipliers',
                  'Full analytics + export',
                  'Complete session history',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Section 6: Pricing */}
        <UpsellPricing onNavigate={handleNavigate} />

        {/* Section 7: Feature Comparison Table */}
        <div id="feature-comparison">
          <UpsellFeatureTable />
        </div>

        {/* Section 8: FAQ */}
        <UpsellFAQ />

        {/* Section 9: Final CTA */}
        <UpsellFinalCta onNavigate={handleNavigate} />

      </div>

      {/* Section 10: Sticky Mobile CTA */}
      <UpsellStickyCta onNavigate={handleNavigate} />
    </div>
  );
};

export default UpsellPage;
